<template>
    <md-layout md-row class="ql-project-search">
        <md-layout md-gutter>
            <form novalidate @submit.stop.prevent="submit">
                <md-menu
                    md-align-trigger
                    :md-manual-toggle="true"
                    class="ql-project-search-box-wrapper"
                    ref="searchResultBox"
                >
                    <md-input-container class="ql-project-search-box-container">
                        <md-autocomplete
                            class="searchBox"
                            placeholder="Search"
                            v-model="term"
                            :min-chars="1"
                            :fetch="search"
                            :prepare-response-data="output"
                            print-attribute="text"
                            @focus="triggerSearch"
                            ref="searchTermField"
                            @selected="selectSearchResult"
                        />
                        <div class="ql-project-search-type-box">
                            <select v-model="searchType" @change="selectSearchType($event.target.value)" class="btn-search-type">
                                <option class="md-list-item md-menu-item md-option" v-for="type in searchTypes" :key="type" :value="type">{{ type }}</option>
                            </select>
                        </div>
                        <md-button class="md-icon-button md-dense ql-project-search-box-button" @click="triggerSearchBoxResult"><md-icon>search</md-icon></md-button>
                    </md-input-container>
                    <md-menu-content :style="searchResultListStyle()" v-if="searchType !== 'script'">
                        <template v-for="(item, index) in searchResult">
                            <md-menu-item v-if="item.type === 'cue'" :key="index" @selected="selectSearchResult(item)">
                                <div class="ql-search-result-item-cue">
                                    <span class="first-line">{{item.label}} <strong>{{item.title}}</strong></span>
                                    <span class="second-line">{{item.subline}}</span>
                                </div>
                                &nbsp;
                                <span>pg {{project.pdfPageNumbers && project.pdfPageNumbers[item.page - 1] && 
                                    project.pdfPageNumbers[item.page - 1].name && project.pdfPageNumbers[item.page - 1].name}}</span>
                            </md-menu-item>
                            <md-menu-item v-else :key="index" @selected="selectSearchResult(item)">
                                <span><strong>{{item.title}}</strong></span>
                                &nbsp;
                                <span>pg {{project.pdfPageNumbers && project.pdfPageNumbers[item.page - 1] && 
                                    project.pdfPageNumbers[item.page - 1].name && project.pdfPageNumbers[item.page - 1].name}}</span>
                            </md-menu-item>
                        </template>
                        <md-menu-item v-show="!searchResult.length" disabled>
                            No {{searchType}} found...
                        </md-menu-item>
                    </md-menu-content>
                    <md-menu-content :style="searchResultListStyle()" v-else class="script-result-items-list">
                        <template v-for="(item, index) in searchResult">
                            <md-menu-item v-if="item.type === 'page'" :key="index" disabled class="script-result-item-page">
                                Page {{project.pdfPageNumbers && project.pdfPageNumbers[item.page - 1] && 
                                    project.pdfPageNumbers[item.page - 1].name && project.pdfPageNumbers[item.page - 1].name}}
                            </md-menu-item>
                            <md-menu-item v-else :key="index" class="script-result-item-line" @selected="selectSearchResult(item)">
                                <div v-html="item.text"></div>
                            </md-menu-item>
                        </template>
                        <md-menu-item v-show="!searchResult.length" disabled>
                            Nothing found...
                        </md-menu-item>
                    </md-menu-content>
                </md-menu>
            </form>
        </md-layout>
    </md-layout>
</template>

<script>
import NavBus from '@/buses/nav';
import { mapGetters } from 'vuex';

const ProjectSearch = {
    name: 'projectSearch',
    data: function() {
        return {
            term: '',
            searchType: 'cues',
            searchTypes: [
                'cues',
                'notes',
                'script'
            ],
            searchResult: [],
            searchFocused: false,
            searchBlurred: false
        };
    },
    computed: {
        ...mapGetters([
            'cues',
            'notes',
            'layerByCue',
            'project'
        ]),
        ...mapGetters({
            pages: 'projectPages'
        }),
        isIpad() {
            return /iPad|Macintosh/i.test(navigator.userAgent) && 'ontouchend' in document;
        }
    },
    methods: {
        search: function({q}) {
            if (this.searchType === 'cues') {
                return this.searchCues(q);
            } else if (this.searchType === 'notes') {
                return this.searchNotes(q);
            } else if (this.searchType === 'script') {
                return this.searchScript(q);
            }
            return Promise.resolve([]);
        },
        searchCues: function (q) {
            return new Promise((resolve) => {
                const cues = this.cues.filter(c => String(c.title + ((c.data && c.data.label) || '')).toLowerCase().includes(q.toLowerCase()))
                    .map((c) => {
                        const layer = this.layerByCue(c);
                        return {
                            page: c.pageNumber,
                            title: String(c.title),
                            label: layer.title,
                            subline: (c.data && c.data.label) || '',
                            type: 'cue'
                        };
                    });
                resolve(cues);
            });
        },
        searchNotes: function (q) {
            return new Promise((resolve) => {
                const notes = this.notes.filter(n => (n.type !== 'free_drawing' && n.type !== 'image' && String(n.text).toLowerCase().includes(q.toLowerCase())))
                    .map((n) => {
                        const pos = String(n.text).toLowerCase().indexOf(q.toLowerCase());

                        const prevCharacters = 20;
                        const nextCharacters = 30;
                        return {
                            page: n.page,
                            title: (pos > prevCharacters ? '...' : '') + String(n.text).substr(Math.max(0, pos - prevCharacters), pos + q.length + nextCharacters) + (pos + q.length < String(n.text).length ? '...' : ''),
                            label: '',
                            type: 'note'
                        };
                    });
                resolve(notes);
            });
        },
        searchScript: function (q) {
            return new Promise((resolve) => {
                let prevPage = 0;
                const scripts = this.pages.filter(({textContent}) => textContent.toLowerCase().includes(q.toLowerCase())).map(({pageNumber, textContent}) => {
                    const positions = [];
                    // The query is plain text, as in the includes() filter above: escape it, or a
                    // "(" or "[" throws here and the search never returns.
                    const regStr = new RegExp(q.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'ig');
                    const matches = textContent.matchAll(regStr);
                    // eslint-disable-next-line no-constant-condition
                    while (1) {
                        const match = matches.next();
                        if (!match.value || match.done) break;
                        positions.push(match.value.index);
                    }

                    let index = 0;
                    let curPos = 0;
                    const characters = 40;

                    let from = 0;
                    let to = 0;

                    const result = [];

                    while (index < positions.length) {
                        from = Math.max(curPos, positions[index] - characters);
                        to = positions[index] + characters;

                        while (to > positions[index + 1]) {
                            to = positions[index + 1] + characters;
                            index++;
                        }

                        result.push({
                            page: pageNumber,
                            text: (from > 0 ? '...' : '') + textContent.substring(from, to).replace(regStr, '<strong>$&</strong>') + (to < textContent.length ? '...' : '')
                        });

                        curPos = to;
                        index++;
                    }

                    return result;
                })
                .flatMap(result => result)
                .sort((a, b) => a.page - b.page)
                .reduce((grouped, item) => {
                    if (item.page !== prevPage) {
                        prevPage = item.page;
                        grouped.push({type: 'page', page: item.page });
                    }
                    grouped.push({
                        type: 'paragraph',
                        ...item
                    });
                    return grouped;
                }, []);
                resolve(scripts);
            });
        },
        output: function (data) {
            this.searchResult = data.sort((a, b) => a.page - b.page);
            this.$refs.searchResultBox.open();
            if (!this.searchFocused) {
                setTimeout(() => {
                    this.$refs.searchTermField.$el.querySelector('input').focus();
                }, 100);
            }
            return [];
        },
        triggerSearch: function() {
            if (!this.searchFocused && this.term && this.searchResult.length) {
                this.$refs.searchResultBox.open();
                this.searchFocused = true;
                setTimeout(() => {
                    if (this.searchBlurred) {
                        this.$refs.searchTermField.$el.querySelector('input').blur();
                        this.searchBlurred = false;
                    } else {
                        this.$refs.searchTermField.$el.querySelector('input').focus();
                    }
                    this.searchFocused = false;
                }, 100);
            }
        },
        selectSearchResult: function(item) {
            if (item) {
                NavBus.$emit('goToPage', item.page);
                this.searchBlurred = true;
            }
            this.$refs.searchResultBox.close();
        },
        selectSearchType: function(type) {
            this.searchType = type;
            this.term = '';
            this.searchResult = [];
        },
        searchResultListStyle() {
            return {
                maxHeight: '400px',
                width: 'auto',
                maxWidth: '450px',
                minWidth: '300px',
                zIndex: '1000 !important',
                marginTop: this.isIpad ? '-81px' : '-35px'
            };
        },
        triggerSearchBoxResult() {
            this.$refs.searchResultBox.open();
        },
        handleKeyDown(e) {
            if (e.ctrlKey && e.altKey && e.code === 'KeyS') {
                this.searchFocused = true;
                setTimeout(() => {
                    this.$refs.searchTermField.$el.querySelector('input').focus();
                    this.searchFocused = false;
                }, 100);
                e.preventDefault();
            }
        }
    },
    mounted: function() {
        document.addEventListener('keydown', this.handleKeyDown);
    },
    created: function() {
    },
    beforeDestroy: function() {
        document.removeEventListener('keydown', this.handleKeyDown);
    }
};

export default ProjectSearch;
</script>

<style lang="scss">
.ql-project-search {
    .btn-search-type {
        text-transform: capitalize;
        margin: 0;
        padding-right: 0;
        min-height: 32px;
        max-height: 32px;
        line-height: 32px;
        border-radius: 0;
        background: rgba(255, 255, 255, 0.3);
        color: #E3E4E5;
        width: 88px;
        padding-left: 17px;
        font-size: 14px;
    }
    .btn-search-type option{
        color: black !important;
        background: rgba(255, 255, 255, 0.3) !important;
    }
    .ql-project-search-box-wrapper {
        width: 100%;
    }
    .ql-project-search-box-container {
        border: solid 1px #aaa;
        border-radius: 4px;
        &:after {
            display: none;
        }
        .ql-project-search-box-button {
            background: #aaa !important;
            margin: 0;
            border-radius: 0;
            .md-ink-ripple {
                border-radius: 0;
            }
            i {
                color: #000 !important;
            }
        }
    }
    .searchBox {
        .md-input {
            padding: 0 8px;
        }
    }
    .ql-project-search-type-box {
        width: 88px;
    }
}
.script-result-items-list {
    .md-list {
        padding: 0;
    }
    .script-result-item-page {
        background: #ccc;
        border-bottom: solid 1px #ccc;
    }
    .script-result-item-line {
        border-bottom: solid 1px #ccc;
        .md-list-item-container {
            white-space: pre-line;
            line-height: 1.2;
            font-size: 14px;
            padding-top: 6px;
            padding-bottom: 6px;
        }
    }
    .md-theme-show-mode.md-list {
        .script-result-item-page {
            background: #555;
        }
    }
}
.ql-search-result-item-cue {
    display: flex;
    flex-direction: column;
    padding: 8px 0;
    span {
        line-height: 1.2;
        white-space: normal;
    }
    .second-line {
        font-size: 0.8em;
    }
}
.md-autocomplete-content {
    background: white;
    opacity: 1;
}

@media only screen and (min-device-width: 768px) and (max-device-width: 1024px) and (orientation: portrait) {
    .ql-project-search {
        .btn-search-type {
            border: unset;
            height: 90px;
        }
    }
}
@media only screen and (min-device-width: 768px) and (max-device-width: 1366px) and (orientation: landscape) {
    .ql-project-search {
        .btn-search-type {
            border: unset;
            height: 90px;
        }
    }
}
</style>


// WEBPACK FOOTER //
// search.vue?41c0cf5f